import Container from "../../../components/Container";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Form, Input, Button, Select, notification } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import { db, auth } from "../../../services/firebase";
import { collection, getDocs, setDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { registrarAtividade } from "../../../services/atividadeService";
import "./styles.css";

const { Password } = Input;
const { Item, useForm } = Form;
const { Option } = Select;

function UserForm() {
  const { id } = useParams();
  const [form] = useForm();

  const [clientes, setClientes] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);

  const getUnidades = (clienteId) => {
    const resultado = clientes.find((cliente) => cliente.id === clienteId);
    setUnidades(resultado?.unidades || []);
    // limpa unidades selecionadas ao trocar de empresa
    if (!id) {
      form.setFieldValue("unidadesCliente", []);
    }
  };

  const fetchClientsFirebase = async () => {
    try {
      const snapshot = await getDocs(collection(db, "clientes"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setClientes(data);
      return data;
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
      return [];
    }
  };

  const fetchUserDetails = async (clientesData) => {
    if (!id) return;

    try {
      const ref = doc(db, "usuarios", id);
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        const userData = snapshot.data();

        // Carrega as unidades do cliente selecionado
        const cliente = clientesData.find((c) => c.id === userData.cliente_id);
        if (cliente) {
          setUnidades(cliente.unidades || []);
        }

        form.setFieldsValue({
          cliente_id: userData.cliente_id,
          unidadesCliente: userData.unidadesCliente || [userData.unidadeCliente],
          nomeUsuario: userData.nomeUsuario,
          loginUsuario: userData.loginUsuario,
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados do usuário:", error);
      notification.error({
        message: "Erro ao carregar usuário",
        description: "Não foi possível carregar os dados do usuário.",
        placement: "topRight",
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const clientesData = await fetchClientsFirebase();
      if (id) {
        await fetchUserDetails(clientesData);
      }
    };
    loadData();
  }, [id]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const unidadesSelecionadas = values.unidadesCliente || [];

      // Busca nome da empresa para salvar junto
      const clienteSelecionado = clientes.find((c) => c.id === values.cliente_id);
      const nomeEmpresa = clienteSelecionado?.nomeEmpresa || "";

      if (id) {
        // EDIÇÃO - Atualiza dados do usuário existente
        const dadosAtualizados = {
          cliente_id: values.cliente_id,
          nomeEmpresa,
          nomeUsuario: values.nomeUsuario,
          loginUsuario: values.loginUsuario,
          unidadeCliente: unidadesSelecionadas[0] || "",
          unidadesCliente: unidadesSelecionadas,
          atualizadoEm: new Date(),
        };

        await updateDoc(doc(db, "usuarios", id), dadosAtualizados);

        // Registrar atividade
        const usuarioLogado = JSON.parse(sessionStorage.getItem("seepilUsuario"));
        await registrarAtividade(
          "Usuário atualizado",
          `${values.nomeUsuario} • ${nomeEmpresa}`,
          usuarioLogado?.nomeUsuario
        );

        notification.success({
          message: "Usuário atualizado!",
          description: `Os dados de ${values.nomeUsuario} foram atualizados com sucesso.`,
          placement: "topRight",
        });
      } else {
        // CADASTRO - Cria novo usuário no Firebase Auth
        const usuario = await createUserWithEmailAndPassword(
          auth,
          values.loginUsuario,
          values.senhaUsuario
        );

        const novoId = usuario.user.uid;

        const dadosUsuario = {
          cliente_id: values.cliente_id,
          nomeEmpresa,
          nomeUsuario: values.nomeUsuario,
          loginUsuario: values.loginUsuario,
          unidadeCliente: unidadesSelecionadas[0] || "",
          unidadesCliente: unidadesSelecionadas,
          tipoUsuario: "cliente",
          criadoEm: new Date(),
          ativo: true,
        };

        await setDoc(doc(db, "usuarios", novoId), dadosUsuario);

        // Registrar atividade
        const usuarioLogado = JSON.parse(sessionStorage.getItem("seepilUsuario"));
        await registrarAtividade(
          "Novo usuário cadastrado",
          `${values.nomeUsuario} • ${nomeEmpresa}`,
          usuarioLogado?.nomeUsuario
        );

        notification.success({
          message: "Usuário cadastrado!",
          description: `O usuário ${values.nomeUsuario} foi cadastrado com sucesso.`,
          placement: "topRight",
        });

        form.resetFields();
        setUnidades([]);
      }
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);

      let mensagemErro = "Não foi possível salvar o usuário.";
      let descricaoErro = "Por favor, tente novamente.";

      if (error.code === "auth/email-already-in-use") {
        mensagemErro = "E-mail já cadastrado";
        descricaoErro = "Este e-mail já está sendo usado por outro usuário. Por favor, utilize um e-mail diferente.";
      } else if (error.code === "auth/invalid-email") {
        mensagemErro = "E-mail inválido";
        descricaoErro = "O formato do e-mail informado não é válido. Verifique e tente novamente.";
      } else if (error.code === "auth/weak-password") {
        mensagemErro = "Senha fraca";
        descricaoErro = "A senha deve ter pelo menos 6 caracteres. Por favor, escolha uma senha mais forte.";
      } else if (error.code === "auth/operation-not-allowed") {
        mensagemErro = "Operação não permitida";
        descricaoErro = "O cadastro de usuários está temporariamente desabilitado. Contate o administrador do sistema.";
      } else if (error.code === "auth/network-request-failed") {
        mensagemErro = "Erro de conexão";
        descricaoErro = "Verifique sua conexão com a internet e tente novamente.";
      } else if (error.code === "permission-denied") {
        mensagemErro = "Permissão negada";
        descricaoErro = "Você não tem permissão para realizar esta operação.";
      }

      notification.error({
        message: mensagemErro,
        description: descricaoErro,
        placement: "topRight",
        duration: 6,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <div className="user-form-container">
        <div className="form-card">
          <div className="form-header">
            <div className="form-header-left">
              <div className="form-icon">
                <UserAddOutlined />
              </div>
              <div>
                <h2 className="form-title">{id ? "Editar" : "Cadastrar"} Usuário</h2>
              </div>
            </div>
          </div>

          <Form onFinish={handleSubmit} layout="vertical" form={form} className="custom-form">
            <Item
              label="Cliente (Empresa)"
              name="cliente_id"
              rules={[{ required: true, message: "Selecione um cliente" }]}
            >
              <Select
                placeholder="Selecione um cliente"
                onChange={getUnidades}
                showSearch
                optionFilterProp="children"
              >
                {clientes.map((cliente) => (
                  <Option key={cliente.id} value={cliente.id}>
                    {cliente.nomeEmpresa}
                  </Option>
                ))}
              </Select>
            </Item>

            <Item
              label="Unidades com Acesso"
              name="unidadesCliente"
              rules={[
                {
                  required: true,
                  message: "Selecione pelo menos uma unidade",
                },
              ]}
              tooltip="Selecione quais unidades este usuário poderá visualizar"
            >
              <Select
                mode="multiple"
                placeholder="Selecione uma ou mais unidades"
                disabled={unidades.length === 0}
              >
                {unidades.map((unidade, index) => (
                  <Option key={index} value={unidade.unidade}>
                    {unidade.unidade}
                  </Option>
                ))}
              </Select>
            </Item>

            <Item
              label="Nome do Usuário"
              name="nomeUsuario"
              rules={[{ required: true, message: "Informe o nome do usuário" }]}
            >
              <Input placeholder="ex: Carlos Garcia" />
            </Item>

            <Item
              label="Login do Usuário (e-mail)"
              name="loginUsuario"
              rules={[
                { required: true, message: "Informe o e-mail do usuário" },
                { type: "email", message: "Informe um e-mail válido" },
              ]}
            >
              <Input placeholder="ex: carlos@empresa.com" disabled={!!id} />
            </Item>

            {!id && (
              <Item
                label="Senha"
                name="senhaUsuario"
                rules={[
                  { required: true, message: "Informe a senha" },
                  { min: 6, message: "A senha deve ter pelo menos 6 caracteres" },
                ]}
              >
                <Password placeholder="Mínimo 6 caracteres" />
              </Item>
            )}

            <div className="form-actions">
              <Button
                type="primary"
                htmlType="submit"
                className="btn-primary"
                loading={loading}
              >
                {id ? "Salvar Alterações" : "Cadastrar Usuário"}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
}

export default UserForm;
